import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  ChevronDown,
  Edit,
  Eye,
  Filter,
  GitBranch,
  MoreVertical,
  Phone,
  Search,
  UserPlus,
} from "lucide-react";

import {
  addSalesRep,
  fetchSalesRepAssigneeLogs,
  fetchSingleSalesRep,
  getSalesRep,
  updateSalesRep,
} from "../../store/slices/epcSlice";
import EditSalesRepModal from "./EditSalesRepModal";
import toast from "react-hot-toast";
import { ISalesRepInfo } from "../../models/epcCustomer.model";
import { ViewCommissionModal } from "../CommissionAndRepayment/VIewCommissionModal";
import SearchInput from "../Search/SearchInput";
import AddSalesRepModal from "./AddSalesRepModal.modal";
import Button, { ButtonType } from "../Assets/Button";
import {
  getRepaymentsBySalesRep,
  getSalesRepCommissionById,
} from "../../store/slices/commissionAndRepaymentSlice";
import ViewRepaymentModal from "../CommissionAndRepayment/ViewRepaymentModal";
import { IStatusCycle } from "../../models/commissionAndRepayment.model";
import { ViewAssigneeLogsModal } from "./ViewSalesRepAssigneeLogsModal";
import useOutsideClick from "../Assets/useOutsideClick";
import PaginationControls from "../pagination/PaginationControls";

export const AllSalesRepInfo: React.FC = () => {
  const dispatch: RootDispatch = useDispatch();
  const salesRepList = useSelector(
    (state: RootState) => state.epc.salesReps || [],
  );
  const selectedSalesRepCommission = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.selectedSalesRepCommission,
  );
  const addingSalesRepLoading = useSelector(
    (state: RootState) => state.epc.loadingState.addingSalesRepLoading,
  );
  const selectedSalesRepRepayments = useSelector(
    (state: RootState) =>
      state.commissionAndRepayment.selectedSalesRepRepayments,
  );

  const salesRepAssigneeLogs = useSelector(
    (state: RootState) => state.epc.salesRepAssigneeLogs || [],
  );

  const fetchingAssigneeLogsLoading = useSelector(
    (state: RootState) => state.epc.loadingState.fetchingAssigneeLogsLoading,
  );

  const totalPages = useSelector(
    (state: RootState) => state.epc.totalPagesOfSalesReps || 1,
  );

  const loading = useSelector(
    (state: RootState) => state.epc.loadingState.fetchingSalesRepLoading,
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isCommissionModalOpen, setIsCommissionModalOpen] = useState(false);
  const [selectedSalesRepId, setSelectedSalesRepId] = useState<string | null>(
    null,
  );
  const [isAddSalesRepOpen, setIsAddSalesRepOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState<string>("");
  const [isRepaymentModalOpen, setIsRepaymentModalOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [selectedSalesRepName, setSelectedSalesRepName] = useState<string>("");

  const [currentPage, setCurrentPage] = useState(1);
  const [isNextLoading, setIsNextLoading] = useState(false);
  const [isPrevLoading, setIsPrevLoading] = useState(false);

  const menuRef = useRef<HTMLDivElement>(null);

  useOutsideClick(
    menuRef,
    () => {
      setOpenMenuId(null);
    },
    "[data-menu-trigger]",
  );

  const fetchSalesReps = async (page: number = 1) => {
    await dispatch(
      getSalesRep({
        search: searchTerm,
        category,
        page,
        limit: 10,
      }),
    );
  };

  useEffect(() => {
    fetchSalesReps(currentPage);
  }, [dispatch, searchTerm, category, currentPage]);

  const role = useSelector((state: RootState) => state.auth.role);

  const toggleMenu = (id: string) => {
    setOpenMenuId((prev) => (prev === id ? null : id));
  };

  const handleViewCommission = async (salesRepId: string) => {
    setSelectedSalesRepId(salesRepId);
    await dispatch(getSalesRepCommissionById({ salesRepId }));
    setIsCommissionModalOpen(true);
    setOpenMenuId(null);
  };

  const handleCommissionFilter = async (startDate: string, endDate: string) => {
    if (selectedSalesRepId) {
      await dispatch(
        getSalesRepCommissionById({
          salesRepId: selectedSalesRepId,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
        }),
      );
    }
  };

  const handleNextPage = async () => {
    if (currentPage >= totalPages) return;
    setIsNextLoading(true);
    const nextPage = currentPage + 1;
    await fetchSalesReps(nextPage);
    setCurrentPage(nextPage);
    setIsNextLoading(false);
  };

  const handlePreviousPage = async () => {
    if (currentPage <= 1) return;
    setIsPrevLoading(true);
    const prevPage = currentPage - 1;
    await fetchSalesReps(prevPage);
    setCurrentPage(prevPage);
    setIsPrevLoading(false);
  };

  const handleViewRepayment = async (salesRepId: string) => {
    setSelectedSalesRepId(salesRepId);
    await dispatch(
      getRepaymentsBySalesRep({
        salesRepId,
      }),
    );
    setIsRepaymentModalOpen(true);
    setOpenMenuId(null);
  };

  const handleViewLogs = async (salesRepId: string, salesRepName: string) => {
    setSelectedSalesRepId(salesRepId);
    setSelectedSalesRepName(salesRepName);
    await dispatch(fetchSalesRepAssigneeLogs(salesRepId));
    setIsLogsModalOpen(true);
    setOpenMenuId(null);
  };

  const handleRepaymentFilter = async (
    startDate: string,
    endDate: string,
    search?: string,
    page: number = 1,
    status?: IStatusCycle,
  ) => {
    if (selectedSalesRepId) {
      await dispatch(
        getRepaymentsBySalesRep({
          salesRepId: selectedSalesRepId,
          page,
          limit: 20,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          search: search || undefined,
          status: status || undefined,
        }),
      );
    }
  };

  const handleEditClick = async (salesRepId: string) => {
    await dispatch(fetchSingleSalesRep(salesRepId));
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleAddSalesRep = async (salesRepData: ISalesRepInfo) => {
    try {
      await dispatch(addSalesRep(salesRepData));
    } catch (error) {
      console.error("Error creating sales rep:", error);
    }
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setCategory(value);
    setCurrentPage(1);
  };

  const handleUpdateSalesRep = async (formData: ISalesRepInfo) => {
    if (!formData._id) return;

    try {
      const allowedCategories = ["Commission Dealer", "Channel Partner"];
      const updatedData = {
        ...formData,
        category: allowedCategories.includes(formData.category)
          ? (formData.category as "Commission Dealer" | "Channel Partner")
          : undefined,
      };

      await dispatch(
        updateSalesRep({ id: formData?._id, updatedData }),
      ).unwrap();

      setIsModalOpen(false);
    } catch (error) {
      toast.error(typeof error === "string" ? error : "Update failed");
    }
  };

  return (
    <div className="overflow-x-hidden">
      <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 flex-1">
            <div className="flex-1 min-w-[250px]">
              <SearchInput
                placeholder="Search by name or mobile number..."
                onSearch={handleSearch}
              />
            </div>

            <div className="relative min-w-[220px]">
              <select
                value={category}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="
            w-full h-10 pl-3 pr-10
            text-sm text-gray-700
            bg-white border border-gray-300 rounded-lg
            focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            transition-all duration-200
          "
              >
                <option value="">All Categories</option>
                <option value="Commission Dealer">Commission Dealer</option>
                <option value="Channel Partner">Channel Partner</option>
              </select>
            </div>
          </div>

          {/* Action Button */}
          {role !== "account" && (
            <Button
              type={ButtonType.Submit}
              onClick={() => setIsAddSalesRepOpen(true)}
              className="
          h-10 px-5
          inline-flex items-center justify-center gap-2
          bg-blue-600 text-white text-sm font-medium
          rounded-lg shadow-sm
          hover:bg-blue-700 hover:shadow-md
          transition-all duration-200
          whitespace-nowrap
        "
            >
              <UserPlus className="w-4 h-4" />
              Add Sales Rep
            </Button>
          )}
        </div>
      </div>

      <div className="w-fullbg-white mx-2 sm:mx-4 overflow-x-scroll rounded-lg border border-gray-200">
        <div className="min-w-[700px] md:min-w-full flex flex-col">
          {/* Table Header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <div className="grid grid-cols-12 gap-3 text-xs font-medium text-gray-700 uppercase tracking-wide">
              <div className="col-span-3">Name</div>
              <div className="col-span-2">Mobile Number</div>
              <div className="col-span-2">PAN Number</div>
              <div className="col-span-2">Company</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-1">Actions</div>
            </div>
          </div>

          {/* Table Body */}
          <div className="flex-1">
            <div className="divide-y divide-gray-100">
              {salesRepList?.length === 0 ? (
                <div className="px-6 py-12 text-center">
                  <div className="text-gray-500 text-sm">
                    No Sales Representative found
                  </div>
                </div>
              ) : (
                salesRepList.map((salesRep: any) => (
                  <div
                    key={salesRep?._id}
                    className="px-4 py-3 hover:bg-gray-50/70 transition-colors"
                  >
                    <div className="grid grid-cols-12 gap-3 items-center">
                      {/* Name */}
                      <div className="col-span-3 font-medium text-sm text-gray-900">
                        {salesRep?.name || "-"}
                      </div>

                      {/* Mobile Number */}
                      <div className="col-span-2 text-sm text-gray-700">
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 mr-2 text-gray-400" />
                          {salesRep?.mobileNumber || "-"}
                        </div>
                      </div>

                      {/* PAN Number */}
                      <div className="col-span-2 text-sm text-gray-700">
                        {salesRep?.panNumber || "-"}
                      </div>

                      {/* Company */}
                      <div className="col-span-2 text-sm text-gray-700">
                        {salesRep?.companyName || "-"}
                      </div>

                      {/* Category */}
                      <div className="col-span-2 text-sm text-gray-700">
                        {salesRep?.category || "-"}
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 relative">
                        <button
                          data-menu-trigger
                          onClick={() => toggleMenu(salesRep?._id)}
                          className="p-2 rounded-md hover:bg-gray-100 transition"
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600" />
                        </button>

                        {openMenuId === salesRep?._id && (
                          <div
                            ref={menuRef}
                            className="absolute right-0 mt-2 w-52 bg-white border border-gray-200 rounded-md shadow-lg z-20"
                          >
                            {role !== "account" && (
                              <button
                                onClick={() => {
                                  handleEditClick(salesRep?._id);
                                  setOpenMenuId(null);
                                }}
                                className="w-full flex items-center gap-2 px-4 py-2 text-xs hover:bg-gray-50"
                              >
                                <Edit className="w-3 h-3 text-gray-500" />
                                Edit
                              </button>
                            )}

                            {salesRep.category === "Commission Dealer" && (
                              <button
                                onClick={() =>
                                  handleViewCommission(salesRep?._id)
                                }
                                className="w-full flex items-center gap-2 px-4 py-2 text-xs hover:bg-gray-50"
                              >
                                <Eye className="w-3 h-3 text-gray-500" />
                                View Commission Detail
                              </button>
                            )}
                            {salesRep.category === "Channel Partner" && (
                              <button
                                onClick={() =>
                                  handleViewRepayment(salesRep?._id)
                                }
                                className="w-full flex items-center gap-2 px-4 py-2 text-xs hover:bg-gray-50"
                              >
                                <Eye className="w-3 h-3 text-gray-500" />
                                View Repayment Detail
                              </button>
                            )}
                            <button
                              onClick={() =>
                                handleViewLogs(salesRep?._id, salesRep?.name)
                              }
                              className="w-full flex items-center gap-2 px-4 py-2 text-xs hover:bg-gray-50"
                            >
                              <GitBranch className="w-3 h-3 text-gray-500" />
                              View Logs
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="p-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <PaginationControls
              isNextFetching={isNextLoading}
              isPrevFetching={isPrevLoading}
              currentPage={currentPage}
              totalPages={totalPages}
              onNext={handleNextPage}
              onPrev={handlePreviousPage}
            />
          </div>
        </div>
      </div>

      <ViewCommissionModal
        isOpen={isCommissionModalOpen}
        onClose={() => setIsCommissionModalOpen(false)}
        commission={selectedSalesRepCommission}
        onFilterChange={handleCommissionFilter}
      />
      <ViewRepaymentModal
        isOpen={isRepaymentModalOpen}
        onClose={() => setIsRepaymentModalOpen(false)}
        data={selectedSalesRepRepayments}
        onFilterChange={handleRepaymentFilter}
      />
      <EditSalesRepModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleUpdateSalesRep}
      />
      <AddSalesRepModal
        isOpen={isAddSalesRepOpen}
        onClose={() => setIsAddSalesRepOpen(false)}
        onSubmit={handleAddSalesRep}
        isLoading={addingSalesRepLoading}
      />
      <ViewAssigneeLogsModal
        isOpen={isLogsModalOpen}
        onClose={() => setIsLogsModalOpen(false)}
        logs={salesRepAssigneeLogs}
        salesRepName={selectedSalesRepName}
        loading={fetchingAssigneeLogsLoading}
      />
    </div>
  );
};
